import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "@/lib/api-client";
import { useApi } from "@/hooks/use-api";
import {
  listOrganizationMembers,
  type OrganizationMemberRecord,
} from "@/lib/organization-client";

type RoutingConditionType =
  | "channel"
  | "keyword"
  | "segment"
  | "tag"
  | "business_hours"
  | "skill"
  | "language"
  | "availability";

interface RoutingCondition {
  type: RoutingConditionType;
  operator: "eq" | "neq" | "contains" | "in";
  value: string;
}

interface RoutingTargetDraft {
  userId: string;
  label: string;
  skills: string;
  languages: string;
}

interface RoutingAction {
  type: "assign_user" | "assign_team" | "round_robin" | "auto_tag";
  userId?: string;
  teamId?: string;
  userPool?: string[];
  targets?: RoutingTargetDraft[];
  tagIds?: string[];
}

interface RoutingRule {
  id: string;
  name: string;
  description: string | null;
  priority: number;
  isActive: boolean;
  conditions: Array<{
    type: RoutingConditionType;
    operator: RoutingCondition["operator"];
    value: string | string[] | boolean;
  }>;
  action: {
    type: RoutingAction["type"];
    userId?: string;
    teamId?: string;
    userPool?: string[];
    tagIds?: string[];
    targets?: Array<{
      userId: string;
      label?: string;
      skills?: string[];
      languages?: string[];
    }>;
  };
}

interface RuleDraft {
  id?: string;
  name: string;
  description: string;
  priority: number;
  isActive: boolean;
  conditions: RoutingCondition[];
  action: RoutingAction;
}

type TeamMember = OrganizationMemberRecord;

interface ConversationTagDefinition {
  id: string;
  name: string;
  color: string;
}

interface AssignmentWorkload {
  assigneeId: string;
  openConversationCount: number;
  availability: "idle" | "active" | "busy";
}

const EMPTY_RULE: RuleDraft = {
  name: "",
  description: "",
  priority: 100,
  isActive: true,
  conditions: [{ type: "channel", operator: "eq", value: "" }],
  action: { type: "assign_user", userId: "", targets: [] },
};

function splitCsv(value: string): string[] {
  return value.split(",").map((item) => item.trim()).filter(Boolean);
}

function summarizeCondition(condition: RoutingRule["conditions"][number]): string {
  const value = Array.isArray(condition.value)
    ? condition.value.join(", ")
    : typeof condition.value === "boolean"
      ? condition.value ? "true" : "false"
      : condition.value;
  return `${condition.type} ${condition.operator} ${value}`;
}

function summarizeAction(action: RoutingRule["action"]): string {
  if (action.type === "auto_tag") {
    return action.tagIds && action.tagIds.length > 0
      ? `Auto-tag ${action.tagIds.length} tag${action.tagIds.length === 1 ? "" : "s"}`
      : "Auto-tag";
  }
  const labels = action.targets?.map((target) => target.label ?? target.userId) ?? [];
  if (action.type === "assign_user") {
    return labels[0] ? `Assign ${labels[0]}` : action.userId ? `Assign user ${action.userId}` : "Assign user";
  }
  if (action.type === "assign_team") {
    return action.teamId ? `Assign team ${action.teamId}` : "Assign team";
  }
  return labels.length > 0 ? `Round robin across ${labels.join(", ")}` : "Round robin";
}

function toDraftFromRule(rule: RoutingRule, membersById: Map<string, TeamMember>): RuleDraft {
  const targets = rule.action.targets?.map((target) => ({
    userId: target.userId,
    label: target.label ?? membersById.get(target.userId)?.name ?? membersById.get(target.userId)?.email ?? target.userId,
    skills: (target.skills ?? []).join(", "),
    languages: (target.languages ?? []).join(", "),
  })) ?? (
    rule.action.userId
      ? [{
          userId: rule.action.userId,
          label: membersById.get(rule.action.userId)?.name ?? membersById.get(rule.action.userId)?.email ?? rule.action.userId,
          skills: "",
          languages: "",
        }]
      : (rule.action.userPool ?? []).map((userId) => ({
          userId,
          label: membersById.get(userId)?.name ?? membersById.get(userId)?.email ?? userId,
          skills: "",
          languages: "",
        }))
  );

  return {
    id: rule.id,
    name: rule.name,
    description: rule.description ?? "",
    priority: rule.priority,
    isActive: rule.isActive,
    conditions: rule.conditions.map((condition) => ({
      ...condition,
      value: Array.isArray(condition.value) ? condition.value.join(", ") : String(condition.value),
    })),
    action: {
      type: rule.action.type,
      userId: rule.action.userId ?? "",
      teamId: rule.action.teamId ?? "",
      userPool: rule.action.userPool ?? [],
      targets,
      tagIds: rule.action.tagIds ?? [],
    },
  };
}

function normalizeDraft(draft: RuleDraft) {
  const targets = (draft.action.targets ?? []).map((target) => ({
    userId: target.userId,
    label: target.label.trim() || undefined,
    skills: splitCsv(target.skills),
    languages: splitCsv(target.languages),
  }));

  return {
    name: draft.name.trim(),
    description: draft.description.trim() || undefined,
    priority: draft.priority,
    isActive: draft.isActive,
    conditions: draft.conditions.map((condition) => ({
      type: condition.type,
      operator: condition.operator,
      value: condition.type === "business_hours"
        ? condition.value === "true"
        : condition.operator === "in"
          || condition.type === "skill"
          || condition.type === "language"
          || condition.type === "availability"
          ? splitCsv(condition.value)
          : condition.value.trim(),
    })),
    action: draft.action.type === "assign_user"
      ? {
          type: "assign_user" as const,
          userId: targets[0]?.userId ?? draft.action.userId?.trim() ?? undefined,
          targets,
        }
      : draft.action.type === "auto_tag"
        ? {
            type: "auto_tag" as const,
            tagIds: draft.action.tagIds ?? [],
          }
      : draft.action.type === "round_robin"
        ? {
            type: "round_robin" as const,
            userPool: targets.map((target) => target.userId),
            targets,
          }
        : {
            type: "assign_team" as const,
            teamId: draft.action.teamId?.trim() || undefined,
          },
  };
}

function availabilityTone(availability: AssignmentWorkload["availability"] | undefined): string {
  if (availability === "busy") {
    return "bg-amber-100 text-amber-700";
  }
  if (availability === "active") {
    return "bg-sky-100 text-sky-700";
  }
  return "bg-emerald-100 text-emerald-700";
}

export default function RoutingRulesPage() {
  const { data, isLoading, error, refetch } = useApi<RoutingRule[]>("/v1/inbox-routing");
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [tags, setTags] = useState<ConversationTagDefinition[]>([]);
  const [workload, setWorkload] = useState<Record<string, AssignmentWorkload>>({});
  const [draft, setDraft] = useState<RuleDraft>(EMPTY_RULE);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [feedbackTone, setFeedbackTone] = useState<"success" | "error">("success");

  useEffect(() => {
    let cancelled = false;

    async function loadTeamContext() {
      try {
        const [membersResponse, workloadResponse, tagsResponse] = await Promise.all([
          listOrganizationMembers(),
          apiFetch<{ data: AssignmentWorkload[] }>("/v1/inbox/assignment-options"),
          apiFetch<{ data: ConversationTagDefinition[] }>("/crm/conversation-tags/definitions"),
        ]);
        if (cancelled) {
          return;
        }
        setMembers(membersResponse);
        setWorkload(Object.fromEntries(workloadResponse.data.map((entry) => [entry.assigneeId, entry])));
        setTags(tagsResponse.data);
      } catch {
        if (!cancelled) {
          setMembers([]);
          setWorkload({});
          setTags([]);
        }
      }
    }

    void loadTeamContext();

    return () => {
      cancelled = true;
    };
  }, []);

  const membersById = useMemo(
    () => new Map(members.map((member) => [member.id, member])),
    [members],
  );

  const rules = data ?? [];
  const isEditing = Boolean(draft.id);
  const selectedTargets = draft.action.targets ?? [];
  const selectedTagIds = draft.action.tagIds ?? [];
  const canSave = draft.name.trim().length > 0
    && draft.conditions.every((condition) => condition.value.trim().length > 0)
    && (
      (draft.action.type === "assign_team" && Boolean(draft.action.teamId?.trim()))
      || (draft.action.type === "assign_user" && selectedTargets.length === 1)
      || (draft.action.type === "round_robin" && selectedTargets.length > 0)
      || (draft.action.type === "auto_tag" && selectedTagIds.length > 0)
    );

  function upsertTarget(member: TeamMember) {
    setDraft((current) => {
      const currentTargets = current.action.targets ?? [];
      const existing = currentTargets.find((target) => target.userId === member.id);
      if (existing) {
        return current;
      }

      const nextTarget: RoutingTargetDraft = {
        userId: member.id,
        label: member.name ?? member.email,
        skills: "",
        languages: "",
      };

      return {
        ...current,
        action: {
          ...current.action,
          userId: current.action.type === "assign_user" ? member.id : current.action.userId,
          targets: current.action.type === "assign_user"
            ? [nextTarget]
            : [...currentTargets, nextTarget],
        },
      };
    });
  }

  function removeTarget(userId: string) {
    setDraft((current) => {
      const nextTargets = (current.action.targets ?? []).filter((target) => target.userId !== userId);
      return {
        ...current,
        action: {
          ...current.action,
          userId: current.action.type === "assign_user" ? (nextTargets[0]?.userId ?? "") : current.action.userId,
          targets: nextTargets,
        },
      };
    });
  }

  function updateTarget(userId: string, field: "skills" | "languages", value: string) {
    setDraft((current) => ({
      ...current,
      action: {
        ...current.action,
        targets: (current.action.targets ?? []).map((target) => (
          target.userId === userId ? { ...target, [field]: value } : target
        )),
      },
    }));
  }

  async function saveRule() {
    if (!canSave) {
      return;
    }

    setIsSaving(true);
    setFeedback(null);
    try {
      const payload = normalizeDraft(draft);
      if (draft.id) {
        await apiFetch(`/v1/inbox-routing/${draft.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/v1/inbox-routing", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setFeedbackTone("success");
      setFeedback(`Routing rule ${draft.id ? "updated" : "created"}.`);
      setDraft(EMPTY_RULE);
      refetch();
    } catch (caughtError) {
      setFeedbackTone("error");
      setFeedback(caughtError instanceof Error ? caughtError.message : "Failed to save routing rule.");
    } finally {
      setIsSaving(false);
    }
  }

  async function deleteRule(ruleId: string) {
    setFeedback(null);
    try {
      await apiFetch(`/v1/inbox-routing/${ruleId}`, { method: "DELETE" });
      setFeedbackTone("success");
      setFeedback("Routing rule deleted.");
      if (draft.id === ruleId) {
        setDraft(EMPTY_RULE);
      }
      refetch();
    } catch (caughtError) {
      setFeedbackTone("error");
      setFeedback(caughtError instanceof Error ? caughtError.message : "Failed to delete routing rule.");
    }
  }

  async function toggleRule(rule: RoutingRule) {
    setFeedback(null);
    try {
      await apiFetch(`/v1/inbox-routing/${rule.id}/toggle`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      setFeedbackTone("success");
      setFeedback(`Routing rule ${rule.isActive ? "disabled" : "enabled"}.`);
      refetch();
    } catch (caughtError) {
      setFeedbackTone("error");
      setFeedback(caughtError instanceof Error ? caughtError.message : "Failed to toggle routing rule.");
    }
  }

  return (
    <div className="max-w-7xl space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-foreground">Inbox Routing Rules</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          Route inbound conversations by channel, language, skill, and live teammate availability. Each rule now persists a real candidate pool with capability metadata, and the webhook runtime uses this exact contract when a new conversation is created.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_420px]">
        <div className="rounded-xl border border-border bg-background">
          <div className="border-b border-border px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Live Rules</h3>
                <p className="mt-1 text-xs text-muted-foreground">Rules are evaluated from lowest priority number to highest. The first actionable match assigns the conversation.</p>
              </div>
              <button
                type="button"
                onClick={() => setDraft(EMPTY_RULE)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
              >
                New rule
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-3 p-5">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-lg border border-border bg-muted/30" />
              ))}
            </div>
          ) : error ? (
            <div className="p-5 text-sm text-red-600">{error}</div>
          ) : rules.length === 0 ? (
            <div className="p-5 text-sm text-muted-foreground">No routing rules exist yet. Create the first rule to start auto-assigning new inbound conversations.</div>
          ) : (
            <div className="divide-y divide-border">
              {rules.map((rule) => (
                <div key={rule.id} className="space-y-4 px-5 py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="text-sm font-semibold text-foreground">{rule.name}</h4>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          rule.isActive ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"
                        }`}>
                          {rule.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      {rule.description ? <p className="mt-1 text-xs text-muted-foreground">{rule.description}</p> : null}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">Priority {rule.priority}</div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-lg bg-surface px-3 py-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Conditions</div>
                      <div className="mt-2 space-y-2 text-sm text-foreground">
                        {rule.conditions.map((condition, index) => (
                          <div key={`${rule.id}-condition-${index}`}>{summarizeCondition(condition)}</div>
                        ))}
                      </div>
                    </div>
                    <div className="rounded-lg bg-surface px-3 py-3">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Action</div>
                      <div className="mt-2 text-sm text-foreground">{summarizeAction(rule.action)}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setDraft(toDraftFromRule(rule, membersById))}
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void toggleRule(rule)}
                      className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground"
                    >
                      {rule.isActive ? "Disable" : "Enable"}
                    </button>
                    <button
                      type="button"
                      onClick={() => void deleteRule(rule.id)}
                      className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="text-sm font-semibold text-foreground">{isEditing ? "Edit Rule" : "Create Rule"}</h3>
          <div className="mt-4 space-y-4">
            <label className="block space-y-1 text-sm">
              <span className="text-xs font-medium uppercase text-muted-foreground">Name</span>
              <input
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <label className="block space-y-1 text-sm">
              <span className="text-xs font-medium uppercase text-muted-foreground">Description</span>
              <textarea
                value={draft.description}
                onChange={(event) => setDraft((current) => ({ ...current, description: event.target.value }))}
                rows={3}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1 text-sm">
                <span className="text-xs font-medium uppercase text-muted-foreground">Priority</span>
                <input
                  type="number"
                  min={1}
                  value={draft.priority}
                  onChange={(event) => setDraft((current) => ({ ...current, priority: Number(event.target.value) || 100 }))}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </label>
              <label className="flex items-end gap-2 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
                />
                <span>Rule active</span>
              </label>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium uppercase text-muted-foreground">Conditions</span>
                <button
                  type="button"
                  onClick={() => setDraft((current) => ({
                    ...current,
                    conditions: [...current.conditions, { type: "keyword", operator: "contains", value: "" }],
                  }))}
                  className="text-xs font-medium text-primary"
                >
                  Add condition
                </button>
              </div>

              {draft.conditions.map((condition, index) => (
                <div key={`draft-condition-${index}`} className="grid gap-2 rounded-lg border border-border bg-surface p-3">
                  <div className="grid gap-2 md:grid-cols-2">
                    <select
                      value={condition.type}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        conditions: current.conditions.map((entry, entryIndex) => (
                          entryIndex === index
                            ? {
                                ...entry,
                                type: event.target.value as RoutingConditionType,
                                operator: ["skill", "language", "availability"].includes(event.target.value) ? "in" : entry.operator,
                                value: event.target.value === "business_hours" ? "true" : entry.value,
                              }
                            : entry
                        )),
                      }))}
                      className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="channel">Channel</option>
                      <option value="keyword">Keyword</option>
                      <option value="segment">Segment</option>
                      <option value="tag">Tag</option>
                      <option value="business_hours">Business hours</option>
                      <option value="skill">Skill</option>
                      <option value="language">Language</option>
                      <option value="availability">Availability</option>
                    </select>
                    <select
                      value={condition.operator}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        conditions: current.conditions.map((entry, entryIndex) => (
                          entryIndex === index ? { ...entry, operator: event.target.value as RoutingCondition["operator"] } : entry
                        )),
                      }))}
                      className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="eq">Equals</option>
                      <option value="neq">Does not equal</option>
                      <option value="contains">Contains</option>
                      <option value="in">In list</option>
                    </select>
                  </div>

                  {condition.type === "business_hours" ? (
                    <select
                      value={condition.value}
                      onChange={(event) => setDraft((current) => ({
                        ...current,
                        conditions: current.conditions.map((entry, entryIndex) => (
                          entryIndex === index ? { ...entry, value: event.target.value } : entry
                        )),
                      }))}
                      className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                    >
                      <option value="true">During business hours</option>
                      <option value="false">Outside business hours</option>
                    </select>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={condition.value}
                        onChange={(event) => setDraft((current) => ({
                          ...current,
                          conditions: current.conditions.map((entry, entryIndex) => (
                            entryIndex === index ? { ...entry, value: event.target.value } : entry
                          )),
                        }))}
                        placeholder={condition.operator === "in" ? "value-a, value-b" : "value"}
                        className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
                      />
                      {draft.conditions.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => setDraft((current) => ({
                            ...current,
                            conditions: current.conditions.filter((_, entryIndex) => entryIndex !== index),
                          }))}
                          className="rounded-md border border-border px-3 py-2 text-xs font-medium text-muted-foreground"
                        >
                          Remove
                        </button>
                      ) : null}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-3 rounded-lg border border-border bg-surface p-3">
              <span className="text-xs font-medium uppercase text-muted-foreground">Action</span>
              <select
                value={draft.action.type}
                onChange={(event) => setDraft((current) => ({
                  ...current,
                  action: event.target.value === "assign_user"
                    ? { type: "assign_user", userId: "", targets: [] }
                    : event.target.value === "assign_team"
                      ? { type: "assign_team", teamId: "" }
                      : event.target.value === "round_robin"
                        ? { type: "round_robin", userPool: [], targets: [] }
                        : { type: "auto_tag", tagIds: [] },
                }))}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <option value="assign_user">Assign user</option>
                <option value="round_robin">Round robin</option>
                <option value="assign_team">Assign team</option>
                <option value="auto_tag">Auto-tag conversation</option>
              </select>

              {draft.action.type === "assign_team" ? (
                <input
                  value={draft.action.teamId ?? ""}
                  onChange={(event) => setDraft((current) => ({
                    ...current,
                    action: { ...current.action, teamId: event.target.value },
                  }))}
                  placeholder="Team ID or slug"
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              ) : draft.action.type === "auto_tag" ? (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Apply these tags automatically when the rule matches a new inbound conversation.
                  </p>
                  {tags.length === 0 ? (
                    <div className="rounded-md border border-dashed border-border px-3 py-3 text-sm text-muted-foreground">
                      No tag definitions found yet. Create tags first in the conversation tags workspace.
                    </div>
                  ) : (
                    <div className="grid gap-2">
                      {tags.map((tag) => {
                        const selected = selectedTagIds.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => setDraft((current) => ({
                              ...current,
                              action: {
                                ...current.action,
                                tagIds: selected
                                  ? (current.action.tagIds ?? []).filter((tagId) => tagId !== tag.id)
                                  : [...(current.action.tagIds ?? []), tag.id],
                              },
                            }))}
                            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left ${
                              selected ? "border-primary bg-primary/5" : "border-border bg-background"
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="text-sm font-medium text-foreground">{tag.name}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{selected ? "Selected" : "Add"}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-muted-foreground">
                    Pick the teammates eligible for this rule. Skills and languages entered here are the metadata the inbound routing runtime uses when matching new conversations.
                  </p>

                  <div className="grid gap-2">
                    {members.map((member) => {
                      const workloadEntry = workload[member.id];
                      const selected = selectedTargets.some((target) => target.userId === member.id);
                      return (
                        <button
                          key={member.id}
                          type="button"
                          onClick={() => selected ? removeTarget(member.id) : upsertTarget(member)}
                          className={`flex items-center justify-between rounded-lg border px-3 py-2 text-left ${
                            selected ? "border-primary bg-primary/5" : "border-border bg-background"
                          }`}
                        >
                          <div>
                            <div className="text-sm font-medium text-foreground">{member.name ?? member.email}</div>
                            <div className="text-xs text-muted-foreground">{member.email} · {member.role}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${availabilityTone(workloadEntry?.availability)}`}>
                              {workloadEntry?.availability ?? "idle"}
                            </span>
                            <span className="text-xs text-muted-foreground">{workloadEntry?.openConversationCount ?? 0} open</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {selectedTargets.length > 0 ? (
                    <div className="space-y-3">
                      {selectedTargets.map((target) => (
                        <div key={target.userId} className="rounded-lg border border-border bg-background p-3">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <div className="text-sm font-medium text-foreground">{target.label}</div>
                              <div className="text-xs text-muted-foreground">{membersById.get(target.userId)?.email ?? target.userId}</div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeTarget(target.userId)}
                              className="rounded-md border border-border px-2 py-1 text-xs text-muted-foreground"
                            >
                              Remove
                            </button>
                          </div>

                          <div className="mt-3 grid gap-3 sm:grid-cols-2">
                            <label className="block space-y-1 text-sm">
                              <span className="text-[11px] font-medium uppercase text-muted-foreground">Skills</span>
                              <input
                                value={target.skills}
                                onChange={(event) => updateTarget(target.userId, "skills", event.target.value)}
                                placeholder="pricing, billing"
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                              />
                            </label>
                            <label className="block space-y-1 text-sm">
                              <span className="text-[11px] font-medium uppercase text-muted-foreground">Languages</span>
                              <input
                                value={target.languages}
                                onChange={(event) => updateTarget(target.userId, "languages", event.target.value)}
                                placeholder="en, es, ro"
                                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void saveRule()}
              disabled={!canSave || isSaving}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {isSaving ? "Saving..." : isEditing ? "Update rule" : "Create rule"}
            </button>
            <button
              type="button"
              onClick={() => setDraft(EMPTY_RULE)}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground"
            >
              Reset
            </button>
          </div>

          {feedback ? (
            <p className={`mt-3 text-sm ${feedbackTone === "success" ? "text-emerald-600" : "text-red-600"}`}>
              {feedback}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
